import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  canAccessRole,
  getSupabaseAdminSession,
  loginWithSupabaseCredentials,
  logoutFromSupabase,
  subscribeToAuthChanges
} from './adminAuthStorage';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const refreshSession = useCallback(async () => {
    try {
      const nextSession = await getSupabaseAdminSession();
      setSession(nextSession);
      setAuthError('');
      return nextSession;
    } catch (error) {
      setSession(null);
      setAuthError(error.message || 'Unable to validate session.');
      return null;
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    const bootstrap = async () => {
      setIsInitializing(true);
      await refreshSession();
      if (isActive) {
        setIsInitializing(false);
      }
    };

    bootstrap();

    const unsubscribe = subscribeToAuthChanges(async (nextAuthSession) => {
      if (!isActive) {
        return;
      }

      if (!nextAuthSession) {
        setSession(null);
        setAuthError('');
        return;
      }

      await refreshSession();
    });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [refreshSession]);

  const login = useCallback(async (credentials) => {
    setIsLoading(true);
    setAuthError('');
    try {
      const nextSession = await loginWithSupabaseCredentials(credentials);
      setSession(nextSession);
      return nextSession;
    } catch (error) {
      setSession(null);
      setAuthError(error.message || 'Unable to sign in.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await logoutFromSupabase();
    setSession(null);
    setAuthError('');
  }, []);

  const canAccess = useCallback(
    (allowedRoles) => canAccessRole(session, allowedRoles),
    [session]
  );

  const value = useMemo(
    () => ({
      session,
      role: session?.role || 'viewer',
      isAuthenticated: Boolean(session),
      isInitializing,
      isLoading,
      authError,
      login,
      logout,
      canAccess
    }),
    [session, isInitializing, isLoading, authError, login, logout, canAccess]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);

  if (!context) {
    throw new Error('useAdminAuth must be used inside AdminAuthProvider.');
  }

  return context;
}
